import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useProjectEntities } from '@/services/query/entities-queries';
import { useProjectTags } from '@/services/query/tags-queries';
import { Filter, X } from 'lucide-react';
import { useState } from 'react';
import { Entity, Tag } from '../types';

interface TimelineFilterProps {
  projectId: string;
  selectedEntities: Entity[];
  selectedTags: Tag[];
  onEntitySelect: (entity: Entity) => void;
  onEntityRemove: (entityId: number) => void;
  onTagSelect: (tag: Tag) => void;
  onTagRemove: (tagId: number) => void;
  onClearFilters: () => void;
}

export function TimelineFilter({
  projectId,
  selectedEntities,
  selectedTags,
  onEntitySelect,
  onEntityRemove,
  onTagSelect,
  onTagRemove,
  onClearFilters
}: TimelineFilterProps) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('entities');

  // Fetch entities and tags
  const { data: entities = [], isLoading: isLoadingEntities } = useProjectEntities(projectId);
  const { data: tags = [], isLoading: isLoadingTags } = useProjectTags(projectId);

  // Filter out already selected entities and tags
  const availableEntities = entities.filter(
    entity => !selectedEntities.some(selected => selected.id === entity.id)
  );

  const availableTags = tags.filter(
    tag => !selectedTags.some(selected => selected.id === tag.id)
  );

  // Group entities by template type
  const groupedEntities = availableEntities.reduce((groups, entity) => {
    const templateName = entity.template_name || 'Other';
    if (!groups[templateName]) {
      groups[templateName] = [];
    }
    groups[templateName].push(entity);
    return groups;
  }, {} as Record<string, Entity[]>);

  // Check if any filters are active
  const hasActiveFilters = selectedEntities.length > 0 || selectedTags.length > 0;

  return (
    <div className="relative">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={hasActiveFilters ? "default" : "ghost"}
                    size="icon"
                    className={`h-9 w-9 rounded-xl ${hasActiveFilters ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted/30"}`}
                  >
                    <Filter className="h-4 w-4" />
                    {hasActiveFilters && (
                      <span className="absolute top-2 right-2 h-2 w-2 rounded-full bg-primary-foreground pointer-events-none" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Filter Timeline {hasActiveFilters ? `(${selectedEntities.length + selectedTags.length} active)` : ''}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="end">
          <Tabs defaultValue={activeTab} onValueChange={setActiveTab}>
            <div className="flex items-center justify-between border-b px-3 py-2">
              <TabsList className="grid w-[160px] grid-cols-2">
                <TabsTrigger value="entities">Entities</TabsTrigger>
                <TabsTrigger value="tags">Tags</TabsTrigger>
              </TabsList>

              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClearFilters}
                  className="h-8 px-2 text-xs"
                >
                  Clear All
                </Button>
              )}
            </div>

            <TabsContent value="entities" className="p-0 m-0">
              <div className="p-3 border-b">
                <h3 className="text-sm font-medium mb-2">Selected Entities</h3>
                {selectedEntities.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No entities selected</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {selectedEntities.map(entity => (
                      <Badge
                        key={entity.id}
                        variant="secondary"
                        className="flex items-center gap-1 px-2 py-1 rounded-lg"
                      >
                        {entity.name}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-4 w-4 p-0 ml-1"
                          onClick={() => onEntityRemove(entity.id)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <ScrollArea className="h-[200px]">
                <div className="p-3">
                  <h3 className="text-sm font-medium mb-2">Available Entities</h3>
                  {isLoadingEntities ? (
                    <p className="text-sm text-muted-foreground">Loading entities...</p>
                  ) : availableEntities.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No entities available</p>
                  ) : (
                    <div className="space-y-3">
                      {Object.entries(groupedEntities).map(([templateName, templateEntities]) => (
                        <div key={templateName}>
                          <h4 className="text-xs font-medium text-muted-foreground mb-1.5">{templateName}</h4>
                          <div className="flex flex-wrap gap-1.5">
                            {templateEntities.map(entity => (
                              <Badge
                                key={entity.id}
                                variant="outline"
                                className="cursor-pointer hover:bg-secondary rounded-lg"
                                onClick={() => onEntitySelect(entity)}
                              >
                                {entity.name}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="tags" className="p-0 m-0">
              <div className="p-3 border-b">
                <h3 className="text-sm font-medium mb-2">Selected Tags</h3>
                {selectedTags.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No tags selected</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {selectedTags.map(tag => (
                      <Badge
                        key={tag.id}
                        variant="secondary"
                        className="flex items-center gap-1 px-2 py-1 rounded-lg"
                        style={{ backgroundColor: tag.color + '20', borderColor: tag.color }}
                      >
                        {tag.name}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-4 w-4 p-0 ml-1"
                          onClick={() => onTagRemove(tag.id)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <ScrollArea className="h-[200px]">
                <div className="p-3">
                  <h3 className="text-sm font-medium mb-2">Available Tags</h3>
                  {isLoadingTags ? (
                    <p className="text-sm text-muted-foreground">Loading tags...</p>
                  ) : availableTags.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No tags available</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {availableTags.map(tag => (
                        <Badge
                          key={tag.id}
                          variant="outline"
                          className="cursor-pointer hover:bg-secondary rounded-lg"
                          style={{ borderColor: tag.color }}
                          onClick={() => onTagSelect(tag)}
                        >
                          {tag.name}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </PopoverContent>
      </Popover>
    </div>
  );
}
